import { useEffect, useMemo, useState } from 'react';
import { Box, FlexBox, Icon, Typography } from '@vp/swan';
import { AGENT_IMAGE_MIME_TYPES } from '../hooks/useClaudeTurn';
import { getHelpRequestDisplayText, isHelpRequestMessage } from '../utils/isHelpRequestMessage';
import { AiAvatar } from './AiAvatar';
import { ComponentMessageView } from './ComponentMessageView';
import type { ImageAttachmentMessage, Message, TextMessage } from '../types/chat';

// ─── TypewriterText ──────────────────────────────────────────────────────────

function TypewriterText({ text, speed = 30 }: { text: string; speed?: number }) {
    const words = useMemo(() => text.split(' '), [text]);
    const [visibleCount, setVisibleCount] = useState(0);

    useEffect(() => {
        if (visibleCount >= words.length) return;
        const timer = setTimeout(() => setVisibleCount((c) => c + 1), speed);
        return () => clearTimeout(timer);
    }, [visibleCount, words.length, speed]);

    // Reset when text changes (new message)
    useEffect(() => {
        setVisibleCount(0);
    }, [text]);

    return (
        <Typography>
            {/* Screen reader gets full text immediately */}
            <span className="sr-only">{text}</span>
            {/* Visual animation hidden from screen readers */}
            <span aria-hidden="true">
                {words.map((word, i) => (
                    <span
                        key={i}
                        className="typewriter-word"
                        style={{
                            opacity: i < visibleCount ? 1 : 0,
                            transition: 'opacity 200ms ease-in',
                        }}
                    >
                        {word}
                        {i < words.length - 1 ? ' ' : ''}
                    </span>
                ))}
            </span>
        </Typography>
    );
}

// ─── Variant subcomponents ───────────────────────────────────────────────────

function resolveDisplayText(message: TextMessage): string {
    if (isHelpRequestMessage(message.content)) return getHelpRequestDisplayText();
    return message.content || '';
}

function TextContent({
    message,
    animate,
    isUser,
}: {
    message: TextMessage;
    animate?: boolean;
    isUser: boolean;
}) {
    const displayText = resolveDisplayText(message);

    if (animate && !isUser) return <TypewriterText text={displayText} />;
    return (
        <Typography style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {displayText}
        </Typography>
    );
}

function ImageAttachmentContent({ message }: { message: ImageAttachmentMessage }) {
    return (
        <Box>
            <FlexBox
                gap={3}
                style={{
                    flexWrap: 'wrap',
                    marginBottom: message.caption ? 'var(--swan-sem-space-3)' : '0',
                }}
            >
                {message.images.map((att) =>
                    AGENT_IMAGE_MIME_TYPES.has(att.mediaType) ? (
                        <img
                            key={att.uploadId}
                            src={att.previewUrl}
                            alt={att.fileName}
                            style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                borderRadius: 'var(--swan-sem-border-radius-standard)',
                                display: 'block',
                            }}
                        />
                    ) : (
                        <Box
                            key={att.uploadId}
                            style={{
                                width: '200px',
                                borderRadius: 'var(--swan-sem-border-radius-standard)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--swan-sem-space-2)',
                                padding: 'var(--swan-sem-space-4)',
                                background: 'var(--swan-sem-color-background-standard)',
                            }}
                        >
                            <Icon iconType="fileGeneric" size="40p" alt={att.fileName} />
                            <Typography
                                fontSkin="body-small"
                                style={{
                                    wordBreak: 'break-all',
                                    textAlign: 'center',
                                }}
                            >
                                {att.fileName}
                            </Typography>
                        </Box>
                    )
                )}
            </FlexBox>
            {message.caption && <Typography>{message.caption}</Typography>}
        </Box>
    );
}

// ─── Dispatch ────────────────────────────────────────────────────────────────

function MessageContent({
    message,
    animate,
    isUser,
}: {
    message: Message;
    animate?: boolean;
    isUser: boolean;
}) {
    switch (message.type) {
        case 'text':
            return <TextContent message={message} animate={animate} isUser={isUser} />;
        case 'imageAttachment':
            return <ImageAttachmentContent message={message} />;
        case 'componentMessage':
            return (
                <ComponentMessageView componentType={message.componentType} data={message.data} />
            );
        case 'componentRequest':
            return null; // Rendered in ChatInputArea, not as a bubble
    }
}

// ─── MessageBubble ───────────────────────────────────────────────────────────

/**
 * A componentMessage representing a confirmed live video call needs tighter
 * bubble padding because the inner card supplies its own background and padding.
 */
function isVideoConfirmationMessage(message: Message): boolean {
    return (
        message.type === 'componentMessage' &&
        message.componentType === 'review-method' &&
        (message.data as { requestType?: unknown }).requestType === 'DesignLive'
    );
}

export function MessageBubble({ message, animate }: { message: Message; animate?: boolean }) {
    const isUser = message.role === 'user';
    const isVideoConfirmation = isVideoConfirmationMessage(message);

    return (
        <FlexBox
            role="group"
            aria-label={isUser ? 'You said' : 'AI assistant said'}
            justifyContent={isUser ? 'flex-end' : 'flex-start'}
            gap={isUser ? 0 : 2}
            marginBottom={5}
        >
            {!isUser && <AiAvatar />}
            <Box
                backgroundColor={isUser ? 'accent' : 'standard'}
                paddingY={4}
                paddingX={isVideoConfirmation ? 4 : 5}
                style={{
                    maxWidth: '576px',
                    borderRadius: isVideoConfirmation
                        ? 'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 var(--swan-sem-border-radius-strong)'
                        : 'var(--swan-sem-border-radius-strong)',
                }}
            >
                <MessageContent message={message} animate={animate} isUser={isUser} />
            </Box>
        </FlexBox>
    );
}
